/**
 * KPISistema.jsx · v101.263.0 · Métricas › KPI del sistema.
 *   /metricas/kpi          → Panel ejecutivo (una tarjeta por indicador: valor, variación y tendencia)
 *   /metricas/kpi/:clave   → Panel de detalle del indicador (widgets que arma el backend)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Loader2, RefreshCw, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { C, OUT, OUT_SM, IN_SM, MONO, card, well, fmt, Spark, Widget } from "../components/KpiWidgets";

const GRUPOS = [
  { nombre: "Operación", claves: ["lead_time", "ot_por_dia", "backlog", "puntualidad", "reincidencia", "cancelaciones"] },
  { nombre: "Agenda y atención al cliente", claves: ["agendamiento", "conversaciones", "atencion_whatsapp"] },
  { nombre: "Contratistas, costos y campo", claves: ["ventas_contratista", "tipos_solucion", "tecnico_contratista", "km"] },
  { nombre: "Calidad e integración con Odoo", claves: ["errores", "cierre_odoo", "fiscalizacion"] },
];
const NOMBRES = {
  lead_time: "Lead Time", agendamiento: "Manual vs autoagendamiento", ventas_contratista: "Ventas por contratista",
  ot_por_dia: "Analítica de OT por día", errores: "Principales errores", km: "Km por sucursal y zona",
  tecnico_contratista: "Técnico vs contratista", conversaciones: "Conversaciones perdidas",
  atencion_whatsapp: "Atención WhatsApp", tipos_solucion: "Tipos de solución y gasto", puntualidad: "Puntualidad",
  backlog: "Backlog", cierre_odoo: "Cierre en Odoo", reincidencia: "Reincidencia", cancelaciones: "Cancelaciones",
  fiscalizacion: "Fiscalización IA",
};
const iso = (d) => d.toISOString().slice(0, 10);
const hace = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const PRESETS = [["7 días", 6], ["30 días", 29], ["90 días", 89]];
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

function useFiltros() {
  const qs = new URLSearchParams(window.location.search);
  const [f, setF] = useState({ desde: qs.get("desde") || hace(29), hasta: qs.get("hasta") || iso(new Date()), sucursal: qs.get("sucursal") || "" });
  return [f, setF];
}

function Barra({ f, setF, sucs, onRefresh, busy }) {
  const dias = Math.round((new Date(f.hasta) - new Date(f.desde)) / 864e5);
  return (
    <div style={{ ...card, flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 14, padding: 18 }}>
      <div style={{ ...well, display: "flex", padding: 4, gap: 4 }}>
        {PRESETS.map(([l, n]) => (
          <button key={l} onClick={() => setF((x) => ({ ...x, desde: hace(n), hasta: iso(new Date()) }))}
            style={{ border: 0, cursor: "pointer", fontSize: 13, padding: "9px 14px", borderRadius: 12, fontWeight: dias === n ? 700 : 600,
              color: dias === n ? C.ink : C.sub, background: dias === n ? C.surf : "transparent", boxShadow: dias === n ? OUT_SM : "none" }}>{l}</button>
        ))}
      </div>
      {[["desde", "Desde"], ["hasta", "Hasta"]].map(([k, l]) => (
        <label key={k} style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>{l}
          <input type="date" value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))}
            style={{ border: 0, padding: "9px 12px", borderRadius: 12, fontSize: 13, color: C.text, background: C.bg, boxShadow: IN_SM }} />
        </label>
      ))}
      <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub, minWidth: 200 }}>Sucursal
        <span style={{ position: "relative" }}>
          <select value={f.sucursal} onChange={(e) => setF((x) => ({ ...x, sucursal: e.target.value }))}
            style={{ appearance: "none", width: "100%", border: 0, padding: "9px 30px 9px 12px", borderRadius: 12, fontSize: 13, fontWeight: 600, color: C.text, background: C.bg, boxShadow: IN_SM }}>
            <option value="">Todas</option>{sucs.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <ChevronDown size={14} color={C.mute} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
        </span>
      </label>
      <button onClick={onRefresh} disabled={busy} style={{ border: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "10px 16px",
        borderRadius: 13, fontWeight: 700, fontSize: 13, color: C.ink, background: C.lime, boxShadow: OUT_SM, marginLeft: "auto" }}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Actualizar
      </button>
    </div>
  );
}

function Delta({ t }) {
  if (t.delta === null || t.delta === undefined || !t.mejor) return null;
  const bueno = t.mejor === "alto" ? t.delta > 0 : t.delta < 0;
  const Icon = t.delta > 0 ? TrendingUp : TrendingDown;
  if (!t.delta) return <span style={{ fontFamily: MONO, fontSize: 11, color: C.mute }}>= período anterior</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: MONO, fontSize: 11, fontWeight: 700,
      padding: "3px 8px", borderRadius: 10, color: bueno ? C.ok : C.bad, background: C.bg, boxShadow: IN_SM }}>
      <Icon size={12} /> {t.delta > 0 ? "+" : ""}{fmt(t.delta)} vs anterior
    </span>
  );
}

function Tarjeta({ t, onOpen, grande }) {
  return (
    <button onClick={onOpen} data-testid={`kpi-card-${t.clave}`}
      style={{ ...card, border: 0, textAlign: "left", cursor: "pointer", padding: 20, gap: 10, gridColumn: grande ? "span 2" : "auto",
        transition: "transform .15s", minHeight: 190 }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: C.sub, textTransform: "uppercase", letterSpacing: ".06em" }}>{NOMBRES[t.clave] || t.titulo}</span>
      {t.error ? (
        <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: C.warn }}><AlertTriangle size={14} /> {t.error}</span>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: grande ? 52 : 38, fontWeight: 800, letterSpacing: "-.03em", color: C.ink, lineHeight: 1 }}>
              {t.unidad === "USD" ? fmt(t.valor, "USD") : fmt(t.valor)}</span>
            {t.unidad && t.unidad !== "USD" && <span style={{ fontSize: 15, fontWeight: 700, color: C.teal }}>{t.unidad}</span>}
          </div>
          <span style={{ fontSize: 12, color: C.sub }}>{t.etiqueta}</span>
          <Delta t={t} />
          <div style={{ marginTop: "auto" }}>{t.spark && <Spark valores={t.spark.valores} color={C.ink} />}</div>
        </>
      )}
    </button>
  );
}

function Ejecutivo() {
  const nav = useNavigate();
  const [f, setF] = useFiltros();
  const [sucs, setSucs] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const cargar = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/v1/kpi/ejecutivo", { params: { ...f, sucursal: f.sucursal || undefined }, timeout: 300000 });
      setData(d);
    } catch (e) { toast.error(errMsg(e, "No se pudo cargar el panel ejecutivo")); } finally { setBusy(false); }
  }, [f]);
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { api.get("/v1/kpi/sucursales").then((r) => setSucs(r.data || [])).catch(() => {}); }, []);
  const porClave = useMemo(() => Object.fromEntries((data?.tarjetas || []).map((t) => [t.clave, t])), [data]);
  const abrir = (k) => nav(`/metricas/kpi/${k}?desde=${f.desde}&hasta=${f.hasta}${f.sucursal ? `&sucursal=${encodeURIComponent(f.sucursal)}` : ""}`);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }} data-testid="kpi-ejecutivo">
      <header>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.mute, letterSpacing: ".12em" }}>MÉTRICAS · KPI DEL SISTEMA</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: C.ink, margin: "4px 0" }}>Panel ejecutivo</h1>
        <p style={{ fontSize: 13, color: C.sub, margin: 0 }}>Las medidas macro de toda la operación. Pulse una tarjeta para ver el detalle del indicador.</p>
      </header>
      <Barra f={f} setF={setF} sucs={sucs} onRefresh={cargar} busy={busy} />
      {!data && busy && <div style={{ ...card, alignItems: "center", color: C.sub }}><Loader2 className="animate-spin" /> Calculando 16 indicadores…</div>}
      {data && GRUPOS.map((g) => (
        <section key={g.nombre} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: C.ink, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <i style={{ width: 8, height: 22, borderRadius: 4, background: C.lime, boxShadow: OUT_SM }} />{g.nombre}</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 18 }}>
            {g.claves.map((k, i) => porClave[k] && <Tarjeta key={k} t={porClave[k]} grande={i === 0} onOpen={() => abrir(k)} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function Detalle({ clave }) {
  const nav = useNavigate();
  const [f, setF] = useFiltros();
  const [sucs, setSucs] = useState([]);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const cargar = useCallback(async (k = clave) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/v1/kpi/panel/${k}`, { params: { ...f, sucursal: f.sucursal || undefined }, timeout: 180000 });
      setP(data);
    } catch (e) { setP(null); toast.error(errMsg(e, "No se pudo cargar el indicador")); } finally { setBusy(false); }
  }, [f, clave]);
  useEffect(() => { cargar(clave); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clave]);
  useEffect(() => { api.get("/v1/kpi/sucursales").then((r) => setSucs(r.data || [])).catch(() => {}); }, []);
  const ir = (k) => nav(`/metricas/kpi/${k}?desde=${f.desde}&hasta=${f.hasta}${f.sucursal ? `&sucursal=${encodeURIComponent(f.sucursal)}` : ""}`);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 230px) minmax(0, 1fr)", gap: 22, alignItems: "start" }} data-testid="kpi-detalle">
      <nav style={{ ...card, padding: 14, gap: 4, position: "sticky", top: 16 }}>
        <button onClick={() => nav(`/metricas/kpi?desde=${f.desde}&hasta=${f.hasta}`)} style={{ border: 0, background: "transparent", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: C.ink, padding: "6px 8px" }}><ArrowLeft size={14} /> Panel ejecutivo</button>
        {GRUPOS.map((g) => (
          <div key={g.nombre} style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: C.mute, textTransform: "uppercase", letterSpacing: ".08em", padding: "0 8px" }}>{g.nombre}</span>
            {g.claves.map((k) => (
              <button key={k} onClick={() => ir(k)} style={{ border: 0, cursor: "pointer", textAlign: "left", fontSize: 13, padding: "8px 10px", borderRadius: 12,
                fontWeight: k === clave ? 700 : 500, color: k === clave ? C.ink : C.sub, background: k === clave ? C.surf : "transparent",
                boxShadow: k === clave ? OUT_SM : "none" }}>{NOMBRES[k]}</button>
            ))}
          </div>
        ))}
      </nav>
      <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
        <header>
          <div style={{ fontFamily: MONO, fontSize: 11, color: C.mute, letterSpacing: ".12em" }}>MÉTRICAS · KPI · DETALLE</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.ink, margin: "4px 0" }}>{p?.titulo || NOMBRES[clave]}</h1>
          {p?.pregunta && <p style={{ fontSize: 14, color: C.sub, margin: 0 }}>{p.pregunta}</p>}
        </header>
        <Barra f={f} setF={setF} sucs={sucs} onRefresh={() => cargar(clave)} busy={busy} />
        {busy && !p && <div style={{ ...card, color: C.sub }}><Loader2 className="animate-spin" /> Calculando…</div>}
        {p && (
          <>
            {p.resumen && (
              <div style={{ ...card, flexDirection: "row", alignItems: "center", gap: 24, flexWrap: "wrap", background: `linear-gradient(135deg, ${C.surf}, #E3EBEF)` }}>
                <div style={{ width: 132, height: 132, borderRadius: "50%", background: C.surf, boxShadow: OUT, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: p.resumen.unidad === "USD" ? 22 : 32, fontWeight: 800, color: C.ink }}>{p.resumen.unidad === "USD" ? fmt(p.resumen.valor, "USD") : fmt(p.resumen.valor)}</span>
                  {p.resumen.unidad !== "USD" && <span style={{ fontSize: 12, fontWeight: 700, color: C.teal }}>{p.resumen.unidad}</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 220 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{p.resumen.etiqueta}</span>
                  <Delta t={p.resumen} />
                  <span style={{ fontSize: 12, color: C.sub, lineHeight: 1.5 }}>{p.fuente}</span>
                  {p.enlace && <a href={p.enlace} style={{ fontSize: 12, fontWeight: 700, color: C.info }}>Abrir el módulo completo →</a>}
                </div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 20 }}>
              {(p.widgets || []).map((w, i) => <Widget key={`${clave}-${i}`} w={w} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function KPISistema() {
  const { clave } = useParams();
  return (
    <Layout wide>
      <div style={{ background: C.bg, borderRadius: 28, padding: "8px 4px" }}>
        {clave ? <Detalle clave={clave} /> : <Ejecutivo />}
      </div>
    </Layout>
  );
}
